import { useId } from 'react';
import { useIntl } from 'react-intl';
import { Dot, Tag, Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { disableAttributionLocations } from '@/helpers/common/attribution.helper';
import { GroupsMenu, getGroupTagStyle } from '../../../../grouping/components';
import { useMultiFlightMap } from '../../../../MultiFlightMap.context';
import {
  buildEnabledAttributionSettings,
  buildEnabledFootfallAttributionSettings,
  needsAttributionSettingsUpdate,
  resolveAttributionMode
} from '../../../../MultiFlightMap.helpers';

import './AttributionAndGroup.scss';

import type { GroupsMenuProps } from '../../../../grouping/components';

type Props = {
  attribution?: {
    active: boolean;
    enabled: boolean;
  };
  /** Groups shown in a hover tooltip (used when `groupMenu` is not provided). */
  groups?: { color?: string; text?: string }[];
  /** Hover / click-opened group menu with edit affordances; replaces the `groups` tooltip when provided. */
  groupMenu?: GroupsMenuProps;
  venueId?: string;
};

export default function AttributionAndGroup({ attribution, groupMenu, groups = [], venueId }: Props) {
  const { formatMessage } = useIntl();
  const attributionFlagId = useId();

  const {
    activeSubsection,
    attributionSettings,
    attributionZones: allAttributionZones,
    createAttributionZonesForVenues,
    setAttributionSettings,
    setAttributionZones,
    temporaryVenues
  } = useMultiFlightMap();

  const handleToggleAttribution = (e?: React.MouseEvent<HTMLSpanElement>) => {
    e?.stopPropagation();
    if (!venueId) {
      return;
    }

    if (attribution?.active) {
      const { noAttributionIsCurrentlyEnabled, updatedAttributionLocations } = disableAttributionLocations(
        allAttributionZones,
        [venueId]
      );
      setAttributionZones(updatedAttributionLocations);
      if (noAttributionIsCurrentlyEnabled) {
        setAttributionSettings({
          ...attributionSettings,
          driveToStore: { enabled: false },
          enabled: false,
          settings: { traffic: false, visits: false }
        });
      }
    } else {
      const venue = temporaryVenues.find(v => v._id === venueId);
      if (venue) {
        const attributionMode = resolveAttributionMode(activeSubsection, temporaryVenues);
        const needsSettingsUpdate = needsAttributionSettingsUpdate(attributionSettings, attributionMode);
        if (needsSettingsUpdate) {
          setAttributionSettings(
            attributionMode === 'footfall'
              ? buildEnabledFootfallAttributionSettings(attributionSettings)
              : buildEnabledAttributionSettings(attributionSettings)
          );
        }
        createAttributionZonesForVenues([venue], attributionMode);
      }
    }
  };

  const attributionTooltipContent = attribution?.enabled ? (
    <div className="attributionAndGroup__attributionTooltip">
      <Dot
        color={attribution.active ? '#21CDCD' : '#9ca3af'}
        label={
          attribution.active
            ? formatMessage({ defaultMessage: 'Enabled attribution', id: 'attributionAndGroup.tooltip.enabled' })
            : formatMessage({ defaultMessage: 'Disabled attribution', id: 'attributionAndGroup.tooltip.disabled' })
        }
      />
      <p className="attributionAndGroup__attributionTooltipAction">
        {attribution.active
          ? formatMessage({ defaultMessage: 'Click to disable', id: 'attributionAndGroup.tooltip.clickToDisable' })
          : formatMessage({ defaultMessage: 'Click to enable', id: 'attributionAndGroup.tooltip.clickToEnable' })}
      </p>
    </div>
  ) : null;

  return (
    <div className="attributionAndGroup">
      {attribution?.enabled && (
        <Tooltip
          config={{
            arrow: true,
            component: attributionTooltipContent,
            forcePlacement: true,
            interactions: [TooltipInteractionEnum.hover],
            placement: 'top',
            portal: true,
            strategy: 'fixed'
          }}
        >
          <Tag
            className="attributionFlag attributionFlag--clickable"
            id={attributionFlagId}
            onClick={handleToggleAttribution}
            status={attribution?.active ? 'enabled' : 'disabled'}
            tiny
          >
            Attribution
          </Tag>
        </Tooltip>
      )}
      {groupMenu ? (
        <GroupsMenu {...groupMenu} />
      ) : (
        groups.length > 0 && (
          <Tooltip
            config={{
              arrow: true,
              component: (
                <ul className="attributionAndGroup__groupList">
                  {groups.map((group, index) => (
                    <li key={index}>{group.text}</li>
                  ))}
                </ul>
              ),
              forcePlacement: true,
              interactions: [TooltipInteractionEnum.hover],
              placement: 'right',
              portal: true,
              strategy: 'fixed'
            }}
          >
            <Tag className="groupFlag" id={'group-flag'} status="custom" tiny {...getGroupTagStyle(groups)} />
          </Tooltip>
        )
      )}
    </div>
  );
}
